import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CollabProposal, ProposalStatus } from '@/types';
import { Button } from '@/components/ui/Button';
import { AlertCircle, Download, PackageCheck, Paperclip, X } from 'lucide-react';

interface WorkReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: CollabProposal;
  onApprove: (proposalId: string) => void;
}

export const WorkReviewModal: React.FC<WorkReviewModalProps> = ({ isOpen, onClose, proposal, onApprove }) => {

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: -30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 30 }}
            className="bg-kala-800 border border-kala-700 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between p-4 border-b border-kala-700 bg-kala-900/50 rounded-t-2xl">
                <h2 className="text-xl font-bold flex items-center gap-2">
                    <PackageCheck className="w-6 h-6 text-blue-400"/> 
                    Review Work for "{proposal.eventName}"
                </h2>
                <button onClick={onClose} className="p-2 rounded-full text-kala-300 hover:bg-kala-700 transition-colors"><X size={22} /></button>
            </header>

            <main className="p-6 space-y-6">
              <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                  <p className="text-sm text-yellow-200">
                      <b className="flex items-center gap-2"><AlertCircle className="w-5 h-5"/> Please Read Before Approving:</b> 
                      Review the submitted files carefully. Approving this work will release the escrowed funds to <b className="font-bold">{proposal.artistName}</b>. This action is final and cannot be undone. 
                      Portal admins will only intervene in disputes based on the strict terms of the agreed-upon contract.
                  </p>
              </div>

              <div>
                  <h3 className="text-md font-medium text-kala-200 mb-2">Submitted Files for Review</h3>
                  {(proposal.submittedWork && proposal.submittedWork.length > 0) ? (
                      <div className="max-h-60 overflow-y-auto space-y-2 rounded-lg border border-kala-700 bg-kala-900/50 p-3">
                          {proposal.submittedWork.map((work, index) => (
                              <div key={index} className="flex items-center justify-between text-sm p-2 bg-kala-800 rounded-md">
                                  <p className="text-kala-300 truncate flex items-center">
                                      <Paperclip className="inline w-4 h-4 mr-2 text-kala-400 flex-shrink-0" />
                                      <span className="truncate font-medium text-white">{work.workDescription || `File ${index + 1}`}</span>
                                  </p>
                                  <a 
                                      href={work.submittedWorkLink} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      className="btn-secondary-sm inline-flex items-center flex-shrink-0 ml-4"
                                  >
                                      <Download className="w-4 h-4 mr-1"/>
                                      Download
                                  </a>
                              </div>
                          ))}
                      </div>
                  ) : (
                      <p className="text-sm text-kala-500 italic text-center py-4">No files have been submitted by the recipient yet.</p>
                  )}
              </div>
            </main>
            
            <footer className="flex justify-between items-center p-4 border-t border-kala-700 bg-kala-900/50 rounded-b-2xl">
                <Button variant="secondary" onClick={onClose}>Close</Button>
                <Button 
                    variant="primary"
                    onClick={() => onApprove(proposal.id)} 
                    disabled={!proposal.submittedWork || proposal.submittedWork.length === 0 || proposal.status === ProposalStatus.EXECUTED}
                    className="bg-green-600 hover:bg-green-500 disabled:bg-green-800/50"
                >
                    <PackageCheck className="w-5 h-5 mr-2" />
                    {proposal.status === ProposalStatus.EXECUTED ? 'Payment Released' : 'Approve Work & Release Payment'}
                </Button>
            </footer>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
